import React, { useState } from 'react';
import { Upload, Trash2, MapPin, FileText, X } from 'lucide-react';
import CustomSelect from '../../../components/ui/CustomSelect';
import CollateralAppraisal from './CollateralAppraisal';

interface CollateralDoc {
  file_url: string;
  title: string;
  notes: string;
}

interface Step9Props {
  formData: any;
  handleChange: (field: string, value: any) => void;
  collateralDocuments: CollateralDoc[];
  onAddCollateralDoc: () => void;
  onUpdateCollateralDoc: (index: number, field: string, value: string) => void;
  onRemoveCollateralDoc: (index: number) => void;
}

export default function Step9Collateral({
  formData,
  handleChange,
  collateralDocuments,
  onAddCollateralDoc,
  onUpdateCollateralDoc,
  onRemoveCollateralDoc,
}: Step9Props) {
  const collateralInfo = formData.collateral_info || {};
  const [docFiles, setDocFiles] = useState<Record<number, string>>({});
  const [ownershipPhoto, setOwnershipPhoto] = useState<string | null>(null);
  const [ownerPhoto, setOwnerPhoto] = useState<string | null>(null);
  const [frontPhoto, setFrontPhoto] = useState<string | null>(null);

  const updateCollateralInfo = (field: string, value: any) => {
    handleChange('collateral_info', { ...collateralInfo, [field]: value });
  };

  const handleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: React.Dispatch<React.SetStateAction<string | null>>,
    field: string
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setter(reader.result as string);
        updateCollateralInfo(field, reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDocFileChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setDocFiles(prev => ({ ...prev, [index]: file.name }));
      onUpdateCollateralDoc(index, 'file_url', file.name);
    }
  };

  const removeDocFile = (index: number) => {
    setDocFiles(prev => {
      const newFiles = { ...prev };
      delete newFiles[index];
      return newFiles;
    });
    onUpdateCollateralDoc(index, 'file_url', '');
  };

  return (
    <div className="space-y-6">
      {/* Info Agunan */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-blue-600">Informasi Agunan</h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Jenis Agunan</label>
          <CustomSelect
            value={collateralInfo.type}
            onChange={v => updateCollateralInfo('type', v)}
            placeholder="Pilih jenis"
            width="w-full"
            color="blue"
            options={[
              { value: 'land_building', label: 'Tanah / Bangunan' },
              { value: 'vehicle', label: 'Kendaraan' },
              { value: 'savings', label: 'Tabungan / Deposito' },
              { value: 'other', label: 'Lainnya' },
            ]}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nama Agunan</label>
            <input
              type="text"
              value={collateralInfo.name || ''}
              onChange={e => updateCollateralInfo('name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nama Pemilik</label>
            <input
              type="text"
              value={collateralInfo.owner_name || ''}
              onChange={e => updateCollateralInfo('owner_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nomor KTP Pemilik
            </label>
            <input
              type="text"
              value={collateralInfo.owner_id || ''}
              onChange={e => updateCollateralInfo('owner_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Jenis Bukti Kepemilikan
            </label>
            <input
              type="text"
              value={collateralInfo.ownership_proof_type || ''}
              onChange={e => updateCollateralInfo('ownership_proof_type', e.target.value)}
              placeholder="Contoh: SHM, BPKB"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nomor Sertifikat</label>
            <input
              type="text"
              value={collateralInfo.certificate_number || ''}
              onChange={e => updateCollateralInfo('certificate_number', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tanggal Kepemilikan
            </label>
            <input
              type="date"
              value={collateralInfo.ownership_date || ''}
              onChange={e => updateCollateralInfo('ownership_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Umur Agunan (Tahun)
            </label>
            <input
              type="number"
              value={collateralInfo.age || ''}
              onChange={e => updateCollateralInfo('age', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Peruntukan / Penggunaan
            </label>
            <input
              type="text"
              value={collateralInfo.usage || ''}
              onChange={e => updateCollateralInfo('usage', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
            />
          </div>

          {/* Upload Foto Bukti Kepemilikan */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Upload Foto Bukti Kepemilikan
            </label>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              id="ownership-proof-photo"
              onChange={e => handleImageUpload(e, setOwnershipPhoto, 'ownership_proof_photo')}
            />
            {ownershipPhoto ? (
              <div className="relative inline-block rounded-lg overflow-hidden border-2 border-gray-200 animate-imageIn">
                <img
                  src={ownershipPhoto}
                  alt="Bukti Kepemilikan"
                  className="max-w-full max-h-40 object-contain"
                />
                <button
                  type="button"
                  onClick={() => {
                    setOwnershipPhoto(null);
                    updateCollateralInfo('ownership_proof_photo', '');
                  }}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all duration-200"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label
                htmlFor="ownership-proof-photo"
                className="flex flex-col items-center justify-center gap-2 w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all duration-300 group"
              >
                <Upload className="w-6 h-6 text-gray-400 group-hover:text-blue-500 transition-all duration-300" />
                <span className="text-xs text-gray-500 group-hover:text-blue-600">Pilih foto</span>
              </label>
            )}
          </div>

          {/* Foto Pemilik */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Foto Pemilik</label>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              id="owner-photo"
              onChange={e => handleImageUpload(e, setOwnerPhoto, 'owner_photo')}
            />
            {ownerPhoto ? (
              <div className="relative inline-block rounded-lg overflow-hidden border-2 border-gray-200 animate-imageIn">
                <img
                  src={ownerPhoto}
                  alt="Foto Pemilik"
                  className="max-w-full max-h-40 object-contain"
                />
                <button
                  type="button"
                  onClick={() => {
                    setOwnerPhoto(null);
                    updateCollateralInfo('owner_photo', '');
                  }}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all duration-200"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label
                htmlFor="owner-photo"
                className="flex flex-col items-center justify-center gap-2 w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all duration-300 group"
              >
                <Upload className="w-6 h-6 text-gray-400 group-hover:text-blue-500 transition-all duration-300" />
                <span className="text-xs text-gray-500 group-hover:text-blue-600">Pilih foto</span>
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Spesifikasi Agunan */}
      <div className="border-t pt-6 space-y-4">
        <h3 className="text-lg font-semibold text-blue-600">Spesifikasi Agunan</h3>

        {!collateralInfo.type && (
          <div className="p-4 bg-amber-50 rounded-lg text-center">
            <p className="text-amber-700">Pilih jenis agunan terlebih dahulu di atas</p>
          </div>
        )}

        {collateralInfo.type === 'land_building' && (
          <div className="space-y-4">
            <h4 className="font-medium">Spesifikasi Tanah / Bangunan</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Luas Tanah (m²)
                </label>
                <input
                  type="number"
                  value={collateralInfo.land_area || ''}
                  onChange={e => updateCollateralInfo('land_area', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Luas Bangunan (m²)
                </label>
                <input
                  type="number"
                  value={collateralInfo.building_area || ''}
                  onChange={e => updateCollateralInfo('building_area', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alamat Agunan
                </label>
                <textarea
                  value={collateralInfo.address || ''}
                  onChange={e => updateCollateralInfo('address', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
                />
              </div>
            </div>
          </div>
        )}

        {collateralInfo.type === 'vehicle' && (
          <div className="space-y-4">
            <h4 className="font-medium">Spesifikasi Kendaraan</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Merk Kendaraan
                </label>
                <input
                  type="text"
                  value={collateralInfo.brand || ''}
                  onChange={e => updateCollateralInfo('brand', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nomor Polisi</label>
                <input
                  type="text"
                  value={collateralInfo.plate_number || ''}
                  onChange={e => updateCollateralInfo('plate_number', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tahun Pembuatan
                </label>
                <input
                  type="number"
                  value={collateralInfo.year || ''}
                  onChange={e => updateCollateralInfo('year', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kapasitas Mesin (CC)
                </label>
                <input
                  type="number"
                  value={collateralInfo.engine_capacity || ''}
                  onChange={e => updateCollateralInfo('engine_capacity', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
                />
              </div>
            </div>
          </div>
        )}

        {collateralInfo.type === 'savings' && (
          <div className="space-y-4">
            <h4 className="font-medium">Spesifikasi Tabungan / Deposito / Surat Berharga</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nama Bank / Instansi Penerbit
                </label>
                <input
                  type="text"
                  value={collateralInfo.bank_name || ''}
                  onChange={e => updateCollateralInfo('bank_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nomor Rekening / Sertifikat
                </label>
                <input
                  type="text"
                  value={collateralInfo.account_number || ''}
                  onChange={e => updateCollateralInfo('account_number', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alamat Bank / Instansi
                </label>
                <textarea
                  value={collateralInfo.bank_address || ''}
                  onChange={e => updateCollateralInfo('bank_address', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
                />
              </div>
            </div>
          </div>
        )}

        {collateralInfo.type === 'other' && (
          <div className="space-y-4">
            <h4 className="font-medium">Spesifikasi Lainnya</h4>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Spesifikasi Lainnya
              </label>
              <textarea
                value={collateralInfo.other_specification || ''}
                onChange={e => updateCollateralInfo('other_specification', e.target.value)}
                rows={4}
                placeholder="Jelaskan spesifikasi agunan..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
              />
            </div>
          </div>
        )}

        {collateralInfo.type && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Catatan</label>
              <textarea
                value={collateralInfo.notes || ''}
                onChange={e => updateCollateralInfo('notes', e.target.value)}
                rows={3}
                placeholder="Catatan tambahan..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
              />
            </div>

            {/* Upload Foto Tampak Depan */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Upload Foto Tampak Depan Agunan
              </label>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                id="front-photo"
                onChange={e => handleImageUpload(e, setFrontPhoto, 'front_photo')}
              />
              {frontPhoto ? (
                <div className="relative inline-block rounded-lg overflow-hidden border-2 border-gray-200 animate-imageIn">
                  <img
                    src={frontPhoto}
                    alt="Tampak Depan"
                    className="max-w-full max-h-48 object-contain"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setFrontPhoto(null);
                      updateCollateralInfo('front_photo', '');
                    }}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all duration-200"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="front-photo"
                  className="flex flex-col items-center justify-center gap-2 w-full px-4 py-8 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all duration-300 group"
                >
                  <Upload className="w-8 h-8 text-gray-400 group-hover:text-blue-500 transition-all duration-300" />
                  <span className="text-sm text-gray-500 group-hover:text-blue-600">
                    Pilih foto tampak depan
                  </span>
                </label>
              )}
            </div>
          </>
        )}
      </div>

      {/* Dokumen Agunan */}
      <div className="border-t pt-6 space-y-4">
        <h3 className="text-lg font-semibold text-blue-600">Dokumen Agunan</h3>

        {collateralDocuments.map((doc, index) => (
          <div key={index} className="border rounded-lg bg-white shadow-sm">
            <div className="p-4 space-y-3">
              <div className="flex justify-between items-start">
                <h4 className="font-medium">Dokumen Agunan #{index + 1}</h4>
                <button
                  type="button"
                  onClick={() => onRemoveCollateralDoc(index)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-200"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-3">
                <input
                  type="file"
                  className="hidden"
                  id={`collateral-doc-${index}`}
                  onChange={e => handleDocFileChange(index, e)}
                />
                {docFiles[index] ? (
                  <div className="flex items-center justify-between p-3 border-2 border-blue-200 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <span className="text-sm text-blue-700 font-medium">{docFiles[index]}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeDocFile(index)}
                      className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all duration-200"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor={`collateral-doc-${index}`}
                    className="flex flex-col items-center justify-center gap-2 w-full px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all duration-300 group"
                  >
                    <Upload className="w-6 h-6 text-gray-400 group-hover:text-blue-500 transition-all duration-300" />
                    <span className="text-sm text-gray-500 group-hover:text-blue-600">
                      Pilih dokumen
                    </span>
                  </label>
                )}

                <input
                  type="text"
                  placeholder="Judul Dokumen"
                  value={doc.title}
                  onChange={e => onUpdateCollateralDoc(index, 'title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
                />

                <textarea
                  placeholder="Catatan"
                  value={doc.notes}
                  onChange={e => onUpdateCollateralDoc(index, 'notes', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
                />
              </div>
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={onAddCollateralDoc}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 border rounded-lg text-gray-600 hover:bg-gray-50 transition-all duration-300"
        >
          <Upload className="w-4 h-4" /> Upload Dokumen Agunan
        </button>
      </div>

      {/* Lokasi Agunan */}
      <div className="border-t pt-6 space-y-4">
        <h3 className="text-lg font-semibold text-blue-600">Lokasi Agunan</h3>
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <MapPin className="w-4 h-4 text-amber-600" />
            Peta Lokasi Agunan
          </label>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
              <input
                type="number"
                step="any"
                value={formData.collateral_latitude || ''}
                onChange={e =>
                  handleChange(
                    'collateral_latitude',
                    e.target.value ? parseFloat(e.target.value) : null
                  )
                }
                placeholder="-6.2088"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
              <input
                type="number"
                step="any"
                value={formData.collateral_longitude || ''}
                onChange={e =>
                  handleChange(
                    'collateral_longitude',
                    e.target.value ? parseFloat(e.target.value) : null
                  )
                }
                placeholder="106.8456"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Taksasi Agunan */}
      <div className="border-t pt-6">
        <CollateralAppraisal
          appraisalData={formData.appraisal_data || {}}
          onChange={data => handleChange('appraisal_data', data)}
        />
      </div>
    </div>
  );
}
